import axios from 'axios'
import config from '@/config'
import { useAppStore } from '@/storage/app'
import { storeToRefs } from 'pinia'
import { useFingerprintStore } from '@/storage/fingerprint'
import type { UserType } from '@/services/api/customer'
import type { GeoJSONType } from '@/storage/site'
import { type DocumentStatus, isDocumentStatus } from '@/types/documents'

export type Site = {
  street: string
  customer: Customer
  id: number
  locationCentroid: [number, number]
  locationBoundary: GeoJSONType
  name: string
  siteDocuments: VerifiedDocument[]
  siteHazards: SiteHazard[]
  uuid: string
  latitude?: number
  longitude?: number
}
export type Customer = {
  connection: string
  email: string
  hasAccreditation: boolean
  hasCheckList: boolean
  hasGPS: boolean
  hasInduction: boolean
  hasNotice: boolean
  hasSpotChecks: boolean
  hasUnregisteredContractor: boolean
  hasVisitorArrivalNotification: boolean
  hasContractorManagement: boolean
  hasWorkOrder: boolean
  id: number
  name: string
  needsPermitPerService: boolean
  phone: string
  postcode: string
  requiresSupervisorSignature: boolean
  state: string
  street: string
  town: string
  customerDocuments?: VerifiedDocument[]
  supervisorChecklist?: string[]
  userTypeIdsRequiringLocation?: number[]
}
export type VerifiedDocument = {
  documentName: string
  id: number
  documentEndpoint?: string
}
export type SiteHazard = {
  description: string
  id: number
}
export type AuthorisedContractorCompany = {
  id: number
  name: string
  userType: UserType
}
export let controller: AbortController = new AbortController()

export type DocumentMeta = {
  createdAt: string | null
  mime: string | null
  name: string | null
  origin: string
  status: DocumentStatus
  uuid: string
  message?: string | null
}

// The backend concatenates its host to the document path without a separator,
// yielding e.g. `https://host.devpdfs/<uuid>`. Reinstate the missing slash.
export const normaliseDocumentOrigin = (origin: string): string => {
  const base = config.api.capture

  if (base && origin.startsWith(base) && origin[base.length] !== '/') {
    return `${base}/${origin.slice(base.length)}`
  }

  return origin
}

// A status this app does not recognise — including none at all, from an api that has not been
// upgraded — is settled once here rather than at each display surface, so that "no status" and
// "explicitly missing" are the same thing everywhere downstream.
export const defaultDocumentStatus = (status: unknown): DocumentStatus =>
  isDocumentStatus(status) ? status : 'missing'

export const getSites = async (
  userId: string,
  userLocation: Position,
  page: string,
  search: string | null = null,
  brandIds: number[] = [],
): Promise<Site[] | undefined> => {
  controller = new AbortController()
  try {
    const response = await axios.get('/api/sites', {
      params: {
        brandIds,
        canBypass: 1,
        page,
        perPage: config.pagination.sites.perPage,
        search,
        userId,
        userLocation,
      },
      signal: controller.signal,
    })

    if (response) {
      return response?.data?.data
    }
    return []
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error.message !== 'canceled') {
      return []
    }
  }
}

export const getNearbySites = async (
  userId: string,
  userLocation: Position,
  radius: number | null = null,
  brandIds: number[] = [],
): Promise<Site[] | undefined> => {
  const response = await axios.get('/api/sites', {
    params: {
      brandIds,
      radius,
      userId,
      userLocation,
    },
  })

  if (response) {
    return response?.data?.data
  }
  return []
}

export const getSiteDocumentMeta = async (
  siteId: number,
  documentId: number,
): Promise<DocumentMeta> =>
  readDocumentMeta(`/api/documents/${documentId}/${siteId}`, documentId)

/**
 * capture-api answers a document it holds no meta for with a 404, and one whose row has no uuid
 * with a 400. Neither is a fault the page should stop for: the document keeps its place and
 * reads as missing. Both are settled here, before the error interceptor would turn them into a
 * page-level failure.
 */
export async function readDocumentMeta(
  url: string,
  documentId: number,
): Promise<DocumentMeta> {
  const response = await axios.get(url, {
    validateStatus: (status) =>
      status === 200 || status === 400 || status === 404,
  })

  if (response.status === 400 || response.status === 404) {
    return {
      createdAt: null,
      mime: null,
      name: null,
      origin: '',
      status: 'missing',
      uuid: `document-${documentId}`,
    }
  }

  const { token } = storeToRefs(useAppStore())
  const { getOs } = storeToRefs(useFingerprintStore())
  const meta: DocumentMeta = response.data.data

  meta.origin =
    normaliseDocumentOrigin(meta.origin) +
    `?token=${token.value}&os=${getOs.value?.toLowerCase()}`
  meta.status = defaultDocumentStatus(meta.status)

  return meta
}

export async function getSite(siteUuid: string): Promise<Site> {
  const res = await axios.get('api/clients/sites/' + siteUuid)

  return res.data.data
}
